/*
Copyright 2026 Chain Care.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import type { PrenigmaTtsControllableMediaElement } from "./voiceControlMediaSession";
import {
  registerPrenigmaTtsMediaPlayback,
  type CancelActivePrenigmaTtsPlaybackOptions,
  type CancelActivePrenigmaTtsPlaybackResult,
  type PrenigmaTtsPlaybackController,
  type PrenigmaTtsPlaybackRegistrationRefs,
} from "./voiceControlTtsPlayback";

export interface PrenigmaTtsPlayableMediaElement
  extends PrenigmaTtsControllableMediaElement {
  load: () => void;
  muted: boolean;
  play: () => Promise<void>;
  preload: string;
  src: string;
}

export interface StartPrenigmaTtsPlaybackSessionOptions {
  audioFactory?: () => PrenigmaTtsPlayableMediaElement;
  audioSource: string;
  controller: PrenigmaTtsPlaybackController | undefined;
  correlationIds?: string[];
  muted?: boolean;
  obsoleteTurnId: string;
  preload?: string;
  refNamespace?: string;
  refs?: Partial<PrenigmaTtsPlaybackRegistrationRefs>;
}

export interface PrenigmaTtsPlaybackSession {
  cancel: (
    options: CancelActivePrenigmaTtsPlaybackOptions,
  ) => CancelActivePrenigmaTtsPlaybackResult;
  playInvoked: boolean;
  playResolved: boolean;
  refs: PrenigmaTtsPlaybackRegistrationRefs;
  registered: true;
  status: "playing";
}

export interface PrenigmaTtsPlaybackSessionUnavailable {
  playInvoked: false;
  playResolved: false;
  registered: false;
  status: "controller_unavailable";
}

export interface PrenigmaTtsPlaybackSessionFailed {
  error: string;
  playInvoked: true;
  playResolved: false;
  refs: PrenigmaTtsPlaybackRegistrationRefs;
  registered: true;
  status: "play_failed";
}

export type StartPrenigmaTtsPlaybackSessionResult =
  | PrenigmaTtsPlaybackSession
  | PrenigmaTtsPlaybackSessionUnavailable
  | PrenigmaTtsPlaybackSessionFailed;

export async function startPrenigmaTtsPlaybackSession({
  audioFactory = defaultAudioFactory,
  audioSource,
  controller,
  correlationIds = [],
  muted = false,
  obsoleteTurnId,
  preload = "auto",
  refNamespace,
  refs,
}: StartPrenigmaTtsPlaybackSessionOptions):
  Promise<StartPrenigmaTtsPlaybackSessionResult> {
  requireText(audioSource, "audioSource");
  if (controller === undefined) {
    return {
      playInvoked: false,
      playResolved: false,
      registered: false,
      status: "controller_unavailable",
    };
  }

  const media = audioFactory();
  media.muted = muted;
  media.preload = preload;
  media.src = audioSource;
  media.load();

  const registration = registerPrenigmaTtsMediaPlayback({
    controller,
    correlationIds,
    media,
    obsoleteTurnId,
    refNamespace,
    refs,
  });
  if (!registration.registered || registration.refs === undefined) {
    return {
      playInvoked: false,
      playResolved: false,
      registered: false,
      status: "controller_unavailable",
    };
  }

  try {
    await media.play();
  } catch (error) {
    controller.clearActivePlayback(registration.refs.obsoleteTurnId);
    return {
      error: error instanceof Error ? error.name || error.message : "play_failed",
      playInvoked: true,
      playResolved: false,
      refs: registration.refs,
      registered: true,
      status: "play_failed",
    };
  }

  return {
    cancel: (options): CancelActivePrenigmaTtsPlaybackResult =>
      controller.cancelActivePlayback(options),
    playInvoked: true,
    playResolved: true,
    refs: registration.refs,
    registered: true,
    status: "playing",
  };
}

function defaultAudioFactory(): PrenigmaTtsPlayableMediaElement {
  if (typeof Audio === "undefined") {
    throw new Error("Audio is unavailable");
  }
  return new Audio() as PrenigmaTtsPlayableMediaElement;
}

function requireText(value: string, path: string): void {
  if (!value.trim()) {
    throw new Error(`${path} is required`);
  }
}
